/**
 * 제목 줄의 이슈 추가. 본 버튼은 이슈를 만들고, 오른쪽 꺽쇠는 다른 종류를 고른다.
 * Task와 플랜은 상단 「만들기」에만 둔다.
 */
import { Caption1, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, makeStyles, tokens } from "@fluentui/react-components";
import { Beaker16Regular, ChevronDown12Regular, Warning16Regular } from "@fluentui/react-icons";
import { FuiButton } from "@/components/fui-control";
import type { RecordKind } from "@/engine/types";
import { RECORD_KIND_HINT, RECORD_KIND_LABEL } from "@/store/record-ops";
import { useProjectStore } from "@/store/project-store";
import { recordKindIcon } from "./record-ui";

const useStyles = makeStyles({
  split: {
    display: "inline-flex",
    alignItems: "center",
  },
  main: {
    whiteSpace: "nowrap",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  chevron: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeftWidth: "1px",
    borderLeftStyle: "solid",
    borderLeftColor: tokens.colorNeutralStrokeOnBrand,
  },
  item: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    whiteSpace: "normal",
  },
});

export function RecordAddMenu({ kinds, onAdd, sample = false }: { kinds: readonly RecordKind[]; onAdd: (kind: RecordKind) => void; sample?: boolean }) {
  const styles = useStyles();
  const others = kinds.filter((kind) => kind !== "issue");
  const hasMenu = others.length > 0 || sample;
  if (!kinds.includes("issue") && !hasMenu) return null;

  return (
    <div className={styles.split}>
      {kinds.includes("issue") ? (
        <FuiButton appearance="primary" className={hasMenu ? styles.main : undefined} icon={<Warning16Regular />} onClick={() => onAdd("issue")}>
          이슈 추가
        </FuiButton>
      ) : null}
      {hasMenu ? (
        <Menu positioning="below-end">
          <MenuTrigger disableButtonEnhancement>
            <FuiButton appearance="primary" className={kinds.includes("issue") ? styles.chevron : undefined} icon={<ChevronDown12Regular />} aria-label="다른 기록" />
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              {others.map((kind) => (
                <MenuItem key={kind} icon={recordKindIcon(kind)} onClick={() => onAdd(kind)}>
                  <span className={styles.item}>
                    <span>{RECORD_KIND_LABEL[kind]}</span>
                    <Caption1 className={styles.hint}>{RECORD_KIND_HINT[kind]}</Caption1>
                  </span>
                </MenuItem>
              ))}
              {sample && (
                <MenuItem icon={<Beaker16Regular />} onClick={() => useProjectStore.getState().appendSampleRecords()}>
                  <span className={styles.item}>
                    <span>목업 기록 넣기</span>
                    <Caption1 className={styles.hint}>개발용 · 여섯 종류 모든 상태의 예시를 지금 플랜에 추가 (되돌리기 가능)</Caption1>
                  </span>
                </MenuItem>
              )}
            </MenuList>
          </MenuPopover>
        </Menu>
      ) : null}
    </div>
  );
}
